import React, { useState } from "react";
import {
  Box,
  Autocomplete,
  TextField,
  Button,
  Chip,
  useMediaQuery,
  useTheme,
  Modal,
  IconButton,
  Typography,
  InputAdornment,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PeopleIcon from "@mui/icons-material/People";

const GroupSelector = ({
  apiGroups,
  selectedGroups,
  setSelectedGroups,
  handleAddGroups,
  searchTerm,
  setSearchTerm,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mode, setMode] = useState("group");
  const [mobileModalOpen, setMobileModalOpen] = useState(false);

  const safeGroups = (apiGroups || []).filter(g => g && g.name);

  const toggleMode = () => {
    setMode(prev => prev === "group" ? "employee" : "group");
    // Clear search when switching modes
    if (mode === "employee") {
      setSearchTerm("");
    }
  };

  const renderGroupAutocomplete = () => (
    <Autocomplete
      multiple
      options={safeGroups}
      value={selectedGroups || []}
      onChange={(e, newValue) => setSelectedGroups(newValue)}
      getOptionLabel={(option) => option.name || ""}
      isOptionEqualToValue={(option, value) => option.groupUid === value.groupUid}
      sx={{ flex: 1, width: "100%" }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            key={index}
            label={option.name}
            {...getTagProps({ index })}
            size="small"
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Search and select groups"
          placeholder="Select"
          size="small"
        />
      )}
    />
  );

  const renderEmployeeSearch = () => (
    <TextField
      fullWidth
      placeholder="Search employees..."
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      size="small"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon color="action" />
          </InputAdornment>
        )
      }}
    />
  );

  const renderMobileGroupModal = () => (
    <Modal
      open={mobileModalOpen}
      onClose={() => setMobileModalOpen(false)}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        p: 2,
        mt: 10
      }}
    >
      <Box
        sx={{
          bgcolor: "background.paper",
          width: "100%",
          maxWidth: 500,
          borderRadius: 2,
          boxShadow: 24
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            borderBottom: 1,
            borderColor: "divider"
          }}
        >
          <Typography variant="h6">
            {mode === "group" ? "Select Groups" : "Search Employees"}
          </Typography>
          <IconButton onClick={() => setMobileModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2 }}>
          {mode === "group" ? renderGroupAutocomplete() : renderEmployeeSearch()}
        </Box>
      </Box>
    </Modal>
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 2,
          mt: 2,
          width: "100%"
        }}
      >
        {isMobile ? (
          <Button
            variant="outlined"
            onClick={() => setMobileModalOpen(true)}
            sx={{
              justifyContent: "space-between",
              textTransform: "none"
            }}
          >
            {mode === "group"
              ? selectedGroups.length
                ? `${selectedGroups.length} group(s) selected`
                : "Select groups"
              : "Search employees"}
          </Button>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              gap: 1
            }}
          >
            {mode === "group" ? renderGroupAutocomplete() : renderEmployeeSearch()}
          </Box>
        )}

        {mode === "group" && (
          <Button
            className="btn"
            variant="contained"
            onClick={handleAddGroups}
            disabled={selectedGroups.length === 0}
          >
            Add Groups
          </Button>
        )}

        <IconButton
          onClick={toggleMode}
          color="primary"
          sx={{
            border: "1px solid",
            borderColor: "divider"
          }}
        >
          {mode === "group" ? <SearchIcon /> : <PeopleIcon />}
        </IconButton>
      </Box>

      {isMobile && renderMobileGroupModal()}
    </>
  );
};

export default GroupSelector;
